import React, { useState, useMemo } from 'react';
import { Check, MousePointerClick, Zap, Star } from 'lucide-react';
import { trackRegister } from '../utils/meta-pixel';

interface Tier {
  minimumRecharge: number;
  mobileTariff: number;
  landlineTariff: number;
}

const TIERS: Tier[] = [
  { minimumRecharge: 50, mobileTariff: 0.37, landlineTariff: 0.08 },
  { minimumRecharge: 500, mobileTariff: 0.35, landlineTariff: 0.08 },
  { minimumRecharge: 1000, mobileTariff: 0.32, landlineTariff: 0.08 },
  { minimumRecharge: 2000, mobileTariff: 0.27, landlineTariff: 0.08 },
  { minimumRecharge: 3000, mobileTariff: 0.21, landlineTariff: 0.08 },
  { minimumRecharge: 5000, mobileTariff: 0.14, landlineTariff: 0.08 },
];

const Pricing: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const sectionRef = React.useRef<HTMLElement>(null);
  
  const selectedTier = TIERS[selectedIndex];

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const progressWidth = useMemo(() => {
    return `${(selectedIndex / (TIERS.length - 1)) * 100}%`;
  }, [selectedIndex]);

  const handleSubscribe = () => {
    trackRegister();
    const appUrl = process.env.REACT_APP_APP_URL || 'https://flow.onmai.app.br';
    window.open(`${appUrl}/register`, '_blank');
  };

  return (
    <section 
      id="pricing" 
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative py-16 lg:py-24 bg-white overflow-hidden group/pricing"
    >
      {/* Background Decor - Modern & Subtle */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_50%_0%,#f8fafc_0%,transparent_50%)]" />
        
        {/* Grid de Linhas Base - Mais visível quando apagado */}
        <div className="absolute inset-0 opacity-[0.04]" 
          style={{ 
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px' 
          }} 
        />

        {/* Grid de Linhas Hover - Mais suave e com mais blur */}
        <div className="absolute inset-0 opacity-0 group-hover/pricing:opacity-[0.12] transition-opacity duration-700" 
          style={{ 
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px',
            WebkitMaskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
            maskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
          }} 
        />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <header className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold mb-4 border border-blue-100">
            <Zap className="w-3 h-3 mr-2 fill-current" />
            CRÉDITOS QUE NÃO EXPIRAM
          </div>
          <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            Escolha quanto quer recarregar
          </h2>
          <p className="text-base text-gray-600 max-w-xl mx-auto">
            Sem taxa de adesão ou custo por usuário. Você paga apenas o minuto da ligação e seus créditos nunca expiram.
          </p>
        </header>

        <div className="max-w-4xl mx-auto">
          {/* Régua de Planos Interativa */}
          <div className="bg-gray-50/50 backdrop-blur-sm rounded-2xl p-6 lg:p-10 border border-gray-100 mb-8">
            <div className="mb-12">
              <p className="text-center text-gray-400 font-medium mb-8 uppercase tracking-widest text-[10px]">
                Selecione o valor da recarga
              </p>
              
              <div className="relative h-24 flex items-center px-2 sm:px-10">
                {/* Trilha da Régua */}
                <div className="absolute left-6 sm:left-10 right-6 sm:right-10 h-1 bg-gray-200 rounded-full">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-500 ease-out"
                    style={{ width: progressWidth }}
                  />
                </div>

                {/* Pontos da Régua */}
                <div className="absolute left-6 sm:left-10 right-6 sm:right-10 flex justify-between items-center h-1">
                  {TIERS.map((tier, index) => {
                    const isSelected = index === selectedIndex;
                    const isReached = index <= selectedIndex;
                    const baseMobile = TIERS[0].mobileTariff;
                    const discount = Math.round((1 - (tier.mobileTariff / baseMobile)) * 100);

                    return (
                      <div key={tier.minimumRecharge} className="relative flex flex-col items-center justify-center">
                        {/* Badge de Desconto */}
                        {discount > 0 && (
                          <div className={`absolute -top-12 px-2 py-0.5 rounded-full text-[8px] sm:text-[9px] font-bold whitespace-nowrap transition-all duration-300
                            ${isSelected ? 'bg-blue-600 text-white scale-110' : 'bg-emerald-500 text-white opacity-80'}`}>
                            {discount}% OFF
                          </div>
                        )}

                        <button
                          onClick={() => setSelectedIndex(index)}
                          className={`z-10 w-5 h-5 sm:w-4 sm:h-4 rounded-full border-2 transition-all duration-300 transform 
                            ${isSelected ? 'border-blue-600 bg-white scale-125 ring-4 ring-blue-600/10' : 
                              isReached ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-gray-200 hover:border-gray-400'}`}
                        />
                        
                        <span className={`absolute top-8 text-[9px] sm:text-xs font-bold whitespace-nowrap transition-colors duration-300
                          ${isSelected ? 'text-blue-600' : 'text-gray-400'} ${index % 2 !== 0 ? 'mt-4 sm:mt-0' : ''}`}>
                          R$ {tier.minimumRecharge >= 1000 ? `${tier.minimumRecharge/1000}k` : tier.minimumRecharge}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Tarifas em destaque */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
              {/* Card Móvel */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-3">
                  <div className="bg-blue-50 p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                    <Star className="w-4 h-4 text-blue-600 fill-current" />
                  </div>
                </div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Tarifa Móvel</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-normal text-gray-900">R$ {selectedTier.mobileTariff.toFixed(2)}</span>
                  <span className="text-gray-500 text-sm">/min</span>
                </div>
                <div className="mt-4 text-[10px] text-gray-400 font-medium">
                  Cadência 0/30/6
                </div>
              </div>

              {/* Card Fixo */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 relative overflow-hidden">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Tarifa Fixo</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-normal text-gray-900">R$ {selectedTier.landlineTariff.toFixed(2)}</span>
                  <span className="text-gray-500 text-sm">/min</span>
                </div>
                <div className="mt-4 text-[10px] text-gray-400 font-medium">
                  Cadência 0/30/6
                </div>
              </div>
            </div>
          </div>

          {/* CTA e Benefícios extras */}
          <div className="text-center">
            <button
              onClick={handleSubscribe}
              className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white font-bold rounded-xl hover:bg-blue-600 transition-all duration-300 hover:scale-105 mb-8 text-base group"
            >
              Receba R$ 1 para testar agora
              <MousePointerClick className="ml-2.5 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left max-w-2xl mx-auto">
              {[
                "Sem taxa de adesão",
                "Sem custo por usuário",
                "Pague apenas o que usar"
              ].map((item) => (
                <div key={item} className="flex items-center text-gray-500 font-medium text-xs">
                  <div className="bg-blue-100 rounded-full p-1 mr-2.5 shrink-0">
                    <Check className="w-2.5 h-2.5 text-blue-600 stroke-[3]" />
                  </div>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing; 
